import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext({
    user: null,           // null = unknown (checking), false = not authed, object = authed
    loading: true,
    login: async () => {},
    logout: async () => {},
    refresh: async () => {},
});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // Fase 13 — refresh proactivo. Cada 20h renovamos el access token en
    // background para que la PWA nunca llegue a un 401 estando en foreground.
    // El interceptor de api.js cubre el caso 401-en-flight; este timer cubre
    // el caso "PWA abierta idle > 24h" y evita el flash a la pantalla de
    // login al primer request post-idle.
    // 20h en vez de 23h para tener margen frente a clock skew server/cliente.
    useEffect(() => {
        if (!user || user === false) return;
        const twentyHoursMs = 20 * 60 * 60 * 1000;
        const t = setInterval(() => {
            api.post("/auth/refresh").catch(() => {
                // refresh murió — el próximo request 401 mandará al login
                // via el interceptor. No tocamos nada aquí.
            });
        }, twentyHoursMs);
        return () => clearInterval(t);
    }, [user]);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            setUser(data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    }, []);

    const logout = useCallback(async () => {
        try { await api.post("/auth/logout"); } catch (_) {}
        // Phase 7.3 — clear the iOS home-screen icon badge so the departing
        // user doesn't leave a stale count for the next agent that logs in
        // on the same device. Feature-detected: no-op on browsers without
        // the Badging API.
        try {
            if (typeof navigator !== "undefined"
                && typeof navigator.clearAppBadge === "function") {
                await navigator.clearAppBadge();
            }
        } catch (_) { /* best-effort */ }
        setUser(false);
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
